import React, { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { ThirdPartyDrugTestResultStatus } from 'models/drugTesting';

import { IThirdPartyTestResult, ITestResultAttachment, REASON_VIEW_MORE_CHAR_LIMIT } from '../types';
import { useStyles } from '../styles';
import ActionsMenu from './ActionsMenu';

interface TestResultCardProps {
  testResult: IThirdPartyTestResult;
  onEdit: (testResult: IThirdPartyTestResult) => void;
  onDelete: (testResult: IThirdPartyTestResult) => void;
  onViewMore: (testResult: IThirdPartyTestResult) => void;
  onOpenAttachment: (attachment: ITestResultAttachment) => void;
}

const TestResultCard: React.FC<TestResultCardProps> = ({
  testResult,
  onEdit,
  onDelete,
  onViewMore,
  onOpenAttachment,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [reasonExpanded, setReasonExpanded] = useState(false);

  const initial = testResult.facilityName.trim().charAt(0).toUpperCase() || 'A';
  const showViewMore = (testResult.reason?.length || 0) > REASON_VIEW_MORE_CHAR_LIMIT;
  // Edit/Delete are only available while the result is Pending — hidden once Approved or Denied.
  const canModify = testResult.resultStatus === ThirdPartyDrugTestResultStatus.Pending;

  // status 2 = Denied (red), status 1 = Approved (green), status 0 = Pending review (amber)
  const getStatusLabel = (status: ThirdPartyDrugTestResultStatus): { text: string; statusClass: string } => {
    if (status === ThirdPartyDrugTestResultStatus.Denied)
      return { text: t('myDrugTesting.status.denied'), statusClass: classes.statusDenied };
    if (status === ThirdPartyDrugTestResultStatus.Approved)
      return { text: t('myDrugTesting.status.approved'), statusClass: classes.statusApproved };
    return { text: t('myDrugTesting.status.pending'), statusClass: classes.statusPending };
  };

  const status = getStatusLabel(testResult.resultStatus);

  const handleMenuOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom - 24, right: window.innerWidth - rect.right + 18 });
    }
    setMenuOpen(true);
  };
  const handleMenuClose = () => setMenuOpen(false);

  return (
    <Box className={classes.appointmentCard} data-testid={`test-result-card-${testResult.id}`}>
      <Box className={classes.cardHeader}>
        <Box className={classes.cardHeaderContent}>
          <Box className={classes.avatar}>{initial}</Box>
          <Box className={classes.facilityInfo}>
            <p className={classes.facilityName} data-testid={`test-result-card-facility-${testResult.id}`}>
              {testResult.facilityName}
            </p>
            <p className={classes.appointmentDate}>{testResult.appointmentDate}</p>
          </Box>
        </Box>
        {canModify && (
          <IconButton
            ref={buttonRef}
            size="small"
            className={classes.menuButton}
            data-testid={`test-result-menu-button-${testResult.id}`}
            onClick={handleMenuOpen}
            aria-label="actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box className={classes.cardDetails}>
        <Box className={classes.detailRow}>
          <p className={classes.detailLabel}>{t('myDrugTesting.card.project')}</p>
          <p className={classes.detailValue}>{testResult.projectName}</p>
        </Box>
        <Box className={classes.detailRow}>
          <p className={classes.detailLabel}>{t('myDrugTesting.card.type')}</p>
          <p className={classes.detailValue}>{testResult.typeName}</p>
        </Box>
        <Box className={classes.detailRow}>
          <p className={classes.detailLabel}>{t('myDrugTesting.card.panel')}</p>
          <p className={classes.detailValue}>{testResult.panelName}</p>
        </Box>
        <Box className={classes.detailRow}>
          <p className={classes.detailLabel}>{t('myDrugTesting.card.reason')}</p>
          <Box className={classes.reasonContainer}>
            <span className={reasonExpanded ? classes.reasonTextExpanded : classes.reasonText}>
              {testResult.reason}
            </span>
            {showViewMore && (
              <span
                className={classes.viewMoreLink}
                data-testid={`test-result-view-more-button-${testResult.id}`}
                onClick={() => setReasonExpanded(!reasonExpanded)}
              >
                {reasonExpanded ? t('myDrugTesting.actions.viewLess') : t('myDrugTesting.actions.viewMore')}
              </span>
            )}
          </Box>
        </Box>

        <Box className={classes.resultStatusRow}>
          <Box className={classes.detailRow}>
            <p className={classes.detailLabel}>{t('myDrugTesting.card.testResult')}</p>
            <p className={classes.detailValue}>{testResult.testResult}</p>
          </Box>
          <Box className={classes.detailRow}>
            <p className={classes.detailLabel}>{t('myDrugTesting.card.resultsStatus')}</p>
            <p className={status.statusClass}>{status.text}</p>
          </Box>
        </Box>

        {testResult.attachments.length > 0 && (
          <Box className={classes.attachmentsSection}>
            <p className={classes.attachmentsLabel}>{t('myDrugTesting.card.attachments')}</p>
            {testResult.attachments.map(attachment => (
              <button
                key={attachment.id}
                type="button"
                className={classes.attachmentLink}
                data-testid={`test-result-attachment-${attachment.id}`}
                onClick={() => onOpenAttachment(attachment)}
              >
                {attachment.fileName}
                <OpenInNewIcon fontSize="inherit" />
              </button>
            ))}
          </Box>
        )}
      </Box>

      {canModify && (
        <ActionsMenu
          isOpen={menuOpen}
          onClose={handleMenuClose}
          position={menuPosition}
          testId="test-result-actions-menu"
          items={[
            {
              label: t('myDrugTesting.contextMenu.editResults'),
              testId: 'test-result-edit-button',
              onClick: () => onEdit(testResult),
            },
            {
              label: t('myDrugTesting.contextMenu.delete'),
              testId: 'test-result-delete-button',
              onClick: () => onDelete(testResult),
            },
          ]}
        />
      )}
    </Box>
  );
};

export default memo(TestResultCard);
